import { memo, useCallback } from 'react';
import { FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Camera } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';
import { EditUserFormData } from '@/hooks/useEditUserForm';
import { toast } from 'sonner';

interface EditUserAvatarSectionProps {
  form: UseFormReturn<EditUserFormData>;
  getInitials: (firstName: string, lastName: string) => string;
  onAvatarFileSelected?: (file: File | null) => void;
  avatarPreviewUrl?: string | null;
  selectedAvatarFile?: File | null;
  currentAvatarUrl: string; // Add this prop to trigger re-renders
  firstName: string;
  lastName: string;
}

/**
 * Profile image tile for the Edit User redesign's left rail: square
 * preview with a camera badge, matching the Create User form.
 */
const EditUserAvatarSection = memo<EditUserAvatarSectionProps>(({
  form,
  getInitials,
  onAvatarFileSelected,
  avatarPreviewUrl,
  selectedAvatarFile,
  currentAvatarUrl,
  firstName,
  lastName
}) => {
  const onAvatarSelection = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;

    if (file && !file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    onAvatarFileSelected?.(file);
  }, [onAvatarFileSelected]);

  const handleRemoveAvatar = useCallback(() => {
    form.setValue('avatar', '', { shouldDirty: true });
    onAvatarFileSelected?.(null);
    const input = document.getElementById('avatar-upload') as HTMLInputElement | null;
    if (input) input.value = '';
  }, [form, onAvatarFileSelected]);

  // Use preview URL if available, otherwise use the prop (which comes from form.watch in parent)
  const displayAvatarUrl = avatarPreviewUrl || currentAvatarUrl;
  const canRemove = Boolean(selectedAvatarFile || currentAvatarUrl);

  return (
    <FormField
      control={form.control}
      name="avatar"
      render={() => (
        <FormItem>
          <div className="rounded-xl border border-border bg-card/40 p-5 flex flex-col items-center text-center">
            <div className="relative">
              <div className="h-28 w-28 rounded-xl border border-border bg-secondary/60 flex items-center justify-center overflow-hidden">
                {displayAvatarUrl ? (
                  <img
                    src={displayAvatarUrl}
                    alt="Profile"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-xl font-semibold">
                    {getInitials(firstName, lastName)}
                  </span>
                )}
              </div>
              <label
                htmlFor="avatar-upload"
                className="absolute -bottom-2 -right-2 flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-border bg-secondary hover:bg-secondary/80"
              >
                <Camera className="h-4 w-4" />
              </label>
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onAvatarSelection}
              />
            </div>
            <div className="mt-4 text-base font-semibold">Profile Image</div>
            <p className="mt-1 text-sm text-muted-foreground">Upload a recent photo.</p>
            {selectedAvatarFile && (
              <p className="mt-3 w-full truncate text-xs text-muted-foreground">
                {selectedAvatarFile.name} (will upload on save)
              </p>
            )}
            {canRemove && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={handleRemoveAvatar}
              >
                Remove
              </Button>
            )}
          </div>
          <FormMessage />
        </FormItem>
      )}
    />
  );
});

EditUserAvatarSection.displayName = 'EditUserAvatarSection';

export default EditUserAvatarSection;
